import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Menu,
  MenuItem,
  Link as MuiLink,
} from '@mui/material';
import {
  ArrowBack,
  MoreVert,
  Share,
  AssignmentReturn,
  Close,
  Edit,
} from '@mui/icons-material';
import proceedingService from '../services/proceedingService';
import { usePermissions } from '../../../hooks/usePermissions';
import PageMasthead from '../../../components/PageMasthead';
import ProceedingModalForm from '../components/ProceedingModalForm';
import { companyService } from '../../companies';
import { retentionService } from '../../retentions';

import ProceedingDocuments from '../components/ProceedingDocuments';
import ProceedingBoxes from '../components/ProceedingBoxes';
import ProceedingExternalUsers from '../components/ProceedingExternalUsers';
import ProceedingLoans from '../components/ProceedingLoans';

const LOAN_LABELS = { custody: 'En custodia', loan: 'En prestamo', returned: 'Devuelto' };

const ProceedingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [proceeding, setProceeding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabIndex, setTabIndex] = useState(0);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [retentions, setRetentions] = useState([]);

  useEffect(() => {
    loadProceeding();
  }, [id]);

  useEffect(() => {
    companyService.getAll({ limit: 100 }).then(res => setCompanies(res.data)).catch(() => {});
  }, []);

  const loadRetentionsForCompany = async (companyId) => {
    try {
      const response = await retentionService.getAll({ companyId, limit: 9999 });
      setRetentions(response.data);
    } catch {
      setRetentions([]);
    }
  };

  const loadProceeding = async () => {
    try {
      setLoading(true);
      const response = await proceedingService.getById(id);
      setProceeding(response.data);
    } catch (error) {
      console.error('Error loading proceeding:', error);
      setError('Error al cargar el expediente');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const handleEditSave = async (data) => {
    try {
      await proceedingService.update(id, data);
      setOpenEditModal(false);
      loadProceeding();
    } catch (error) {
      throw error;
    }
  };

  const handleCloseProceeding = async () => {
    if (!window.confirm('¿Cerrar este expediente? Se establecera la fecha de cierre a hoy.')) return;
    try {
      await proceedingService.update(id, { endDate: new Date().toISOString().split('T')[0] });
      loadProceeding();
      setMenuAnchor(null);
    } catch (err) {
      console.error('Error closing proceeding:', err);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !proceeding) {
    return (
      <Box>
        <Alert severity="error">{error || 'Expediente no encontrado'}</Alert>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }} startIcon={<ArrowBack />}>
          Volver
        </Button>
      </Box>
    );
  }

  const formatDate = (date) => {
    if (!date) return '\u2014';
    return new Date(date).toISOString().split('T')[0];
  };

  const loanStatus = proceeding.loan || 'custody';

  const InfoField = ({ label, children }) => (
    <Box>
      <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(29,31,32,0.55)', mb: 0.5 }}>
        {label}
      </Typography>
      <Box>{children}</Box>
    </Box>
  );

  return (
    <Box>
      {/* Masthead */}
      <PageMasthead
        title={`${proceeding.name} - ${proceeding.code}`}
        subtitle={`Expediente · ID ${proceeding.id}`}
      />

      {/* Back + Actions row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/proceedings')}
          sx={{ color: '#5980a6', fontWeight: 700, fontSize: '0.9375rem' }}
        >
          Expedientes
        </Button>

        <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{ width: 36, height: 36 }}>
          <MoreVert sx={{ fontSize: 20 }} />
        </IconButton>
        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
          {hasPermission('proceeding.update') && (
            <MenuItem onClick={() => { setMenuAnchor(null); setOpenEditModal(true); if (proceeding?.companyId) loadRetentionsForCompany(proceeding.companyId); }}>
              <Edit fontSize="small" sx={{ mr: 1 }} /> Editar expediente
            </MenuItem>
          )}
          {hasPermission('proceeding.share') && (
            <MenuItem onClick={() => { setMenuAnchor(null); setTabIndex(2); }}>
              <Share fontSize="small" sx={{ mr: 1 }} /> Compartir expediente
            </MenuItem>
          )}
          {hasPermission('proceeding.ask_physical') && (
            <MenuItem onClick={() => { setMenuAnchor(null); setTabIndex(3); }}>
              <AssignmentReturn fontSize="small" sx={{ mr: 1 }} /> Solicitar en fisico
            </MenuItem>
          )}
          {hasPermission('proceeding.close') && (
            <MenuItem onClick={handleCloseProceeding} disabled={!!proceeding.endDate}>
              <Close fontSize="small" sx={{ mr: 1 }} /> Cerrar expediente
            </MenuItem>
          )}
        </Menu>
      </Box>

      {/* Info card */}
      <Paper sx={{ p: { xs: 2.5, sm: 3.5 }, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#5980a6', mb: 0.5 }}>
              Nombre / Codigo
            </Typography>
            <Typography variant="h4" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              {proceeding.name} - {proceeding.code}
            </Typography>
          </Box>
          <Chip
            label={LOAN_LABELS[loanStatus] || loanStatus}
            size="small"
            variant="outlined"
            sx={{ borderColor: 'rgba(29,31,32,0.16)', flexShrink: 0 }}
          />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: '20px', mb: 3 }}>
          <InfoField label="Empresa">
            <Typography variant="body1">{proceeding.company?.name || '\u2014'}</Typography>
          </InfoField>
          <InfoField label="Anotaciones">
            <Typography variant="body1">{proceeding.description || proceeding.companyTwo || '\u2014'}</Typography>
          </InfoField>
          <InfoField label="Fecha Inicial">
            <Typography variant="body1">{formatDate(proceeding.startDate)}</Typography>
          </InfoField>
          {proceeding.endDate && (
            <InfoField label="Fecha de cierre">
              <Typography variant="body1">{formatDate(proceeding.endDate)}</Typography>
            </InfoField>
          )}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: '20px' }}>
          <InfoField label="Tabla de Retencion">
            <Typography variant="body1">{proceeding.retentionLine?.retention?.name || '\u2014'}</Typography>
          </InfoField>
          {proceeding.retentionLine && (
            <>
              <InfoField label="Serie">
                <MuiLink component={RouterLink} to={`/retentions/lines/${proceeding.retentionLine.id}`} sx={{ color: '#5980a6' }}>
                  {proceeding.retentionLine.series}
                </MuiLink>
              </InfoField>
              <InfoField label="Subserie">
                <MuiLink component={RouterLink} to={`/retentions/lines/${proceeding.retentionLine.id}`} sx={{ color: '#5980a6' }}>
                  {proceeding.retentionLine.subseries}
                </MuiLink>
              </InfoField>
            </>
          )}
        </Box>
      </Paper>

      {/* Tabs */}
      <Box sx={{ mb: 3, display: 'inline-flex' }}>
        <Tabs value={tabIndex} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab label="Documentos" />
          <Tab label="Cajas" />
          <Tab label="Compartido con" />
          <Tab label="Prestamos" />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <Box>
        {tabIndex === 0 && (
          <ProceedingDocuments
            proceedingId={proceeding.id}
            documents={proceeding.documents}
            onUpdate={loadProceeding}
            isClosed={!!proceeding.endDate}
          />
        )}
        {tabIndex === 1 && (
          <ProceedingBoxes
            proceedingId={proceeding.id}
            companyId={proceeding.companyId}
            boxes={proceeding.boxes}
            onUpdate={loadProceeding}
          />
        )}
        {tabIndex === 2 && (
          <ProceedingExternalUsers
            proceedingId={proceeding.id}
            users={proceeding.sharedWith}
            onUpdate={loadProceeding}
          />
        )}
        {tabIndex === 3 && (
          <ProceedingLoans
            proceedingId={proceeding.id}
            companyId={proceeding.companyId}
            loans={proceeding.loans}
            onUpdate={loadProceeding}
          />
        )}
      </Box>

      <ProceedingModalForm
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        onSave={handleEditSave}
        proceeding={proceeding}
        companies={companies}
        retentions={retentions}
        onCompanyChange={loadRetentionsForCompany}
      />
    </Box>
  );
};

export default ProceedingDetail;
